import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, Edit3, Radio, Trash2, XCircle, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmt(dt) { return new Date(dt).toLocaleString(); }

function statusBadge(status) {
  if (status === "cancelled") return "bg-destructive/15 text-destructive";
  if (status === "completed") return "bg-emerald-500/15 text-emerald-600";
  return "bg-secondary text-muted-foreground";
}

function LiveRow({ s, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: s.title, trainer: s.trainer,
    starts_at_local: new Date(s.starts_at).toISOString().slice(0, 16),
    zoom_link: s.zoom_link, course_slug: s.course_slug || "",
  });

  async function save() {
    try {
      await api.put(`/admin/live-sessions/${s.id}`, {
        title: form.title, trainer: form.trainer,
        starts_at: new Date(form.starts_at_local).toISOString(),
        zoom_link: form.zoom_link, course_slug: form.course_slug,
      });
      toast.success("✓ Changes Saved Successfully"); setEditing(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function del() {
    try { await api.delete(`/admin/live-sessions/${s.id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function duplicate() {
    try { await api.post(`/admin/live-sessions/${s.id}/duplicate`); toast.success("Duplicated"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function setStatus(status) {
    try {
      await api.put(`/admin/live-sessions/${s.id}`, { status });
      toast.success(`Marked ${status}`); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  const badge = statusBadge(s.status);

  return (
    <div className="px-5 py-4 border-b border-border last:border-0" data-testid={`live-row-${s.id}`}>
      {!editing ? (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm truncate">{s.title}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${badge}`}>{s.status || "scheduled"}</span>
            </div>
            <p className="text-xs text-muted-foreground">{fmt(s.starts_at)} · {s.trainer}</p>
            <a href={s.zoom_link} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline break-all">{s.zoom_link}</a>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`live-edit-${s.id}`} className="rounded-full">
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" onClick={duplicate} data-testid={`live-duplicate-${s.id}`} className="rounded-full">
              <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStatus("completed")} className="rounded-full text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStatus("cancelled")} className="rounded-full text-amber-600">
              <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-destructive rounded-full" data-testid={`live-delete-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{s.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>This session will be removed for all students immediately. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-xl h-11" /></div>
          <div><Label>Trainer</Label>
            <Input value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} className="mt-1.5 rounded-xl h-11" /></div>
          <div><Label>Starts at</Label>
            <Input type="datetime-local" value={form.starts_at_local}
                   onChange={(e) => setForm({ ...form, starts_at_local: e.target.value })}
                   className="mt-1.5 rounded-xl h-11" /></div>
          <div className="sm:col-span-2"><Label>Zoom link</Label>
            <Input value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} className="mt-1.5 rounded-xl h-11" /></div>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={save} data-testid={`live-save-${s.id}`} className="royal-gradient border-0 text-white rounded-full">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLive() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ title: "", trainer: "", starts_at: "", zoom_link: "", course_slug: "" });
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/live-sessions");
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Prefill defaults from settings
  useEffect(() => {
    setForm((f) => ({
      ...f,
      trainer: f.trainer || settings.default_trainer_name || "",
      zoom_link: f.zoom_link || settings.zoom_default_link || "",
    }));
  }, [settings]);

  async function schedule() {
    if (!form.title || !form.starts_at || !form.zoom_link) return toast.error("Title, date and Zoom link required");
    try {
      await api.post("/admin/live-sessions", form);
      toast.success("✓ Changes Saved Successfully");
      setForm({ ...form, title: "", starts_at: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-live">
      <div className="rounded-3xl bg-card border border-border p-6 space-y-4">
        <h3 className="font-display text-xl font-bold">Schedule a live class</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="live-title" className="mt-1.5 rounded-xl h-11" /></div>
          <div><Label>Trainer</Label><Input value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} className="mt-1.5 rounded-xl h-11" /></div>
          <div><Label>Starts at</Label>
            <Input type="datetime-local" data-testid="live-startsat"
              onChange={(e) => setForm({ ...form, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="mt-1.5 rounded-xl h-11" /></div>
          <div><Label>Zoom link</Label><Input value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} data-testid="live-zoom" className="mt-1.5 rounded-xl h-11" /></div>
        </div>
        <Button onClick={schedule} data-testid="live-schedule" className="rounded-full royal-gradient border-0 text-white btn-lift">
          <Radio className="h-4 w-4 mr-1.5" /> Schedule
        </Button>
      </div>

      <div className="rounded-3xl bg-card border border-border overflow-hidden">
        <p className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">All sessions</p>
        {items.map((s) => <LiveRow key={s.id} s={s} onChange={load} />)}
        {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No sessions yet.</div>}
      </div>
    </div>
  );
}
